import React, {Fragment, useState} from 'react';
import {withRouter} from 'react-router-dom';
import {connect, MapDispatchToProps, MapStateToProps} from 'react-redux';
import {TextField} from '@material-ui/core';
import {Modals} from "../../component";
import {rejectAttorney} from "../../vendors/actions/case";
import {showRejectAttorneyModal} from "../../vendors/actions/modals";
import {UNMATCH_WINDOW_HOURS} from "../../vendors/utils/common";

/**
 * "Reject Attorney" - the consumer asks to be unmatched from the lawyer on the case.
 *
 * The reason is required: it is what the lawyer reads when deciding whether to accept, and
 * what an AppearMe admin reads if they object. Nothing is canceled at this point - the case
 * moves to unmatch pending (20) and the lawyer gets 6 BUSINESS hours to answer, which the
 * copy spells out because a Friday-afternoon request will not run out until Monday.
 *
 * Two components on purpose. Every file in layout/modals is mounted for the whole session
 * (layout/index.tsx renders all of them and each one returns null until its flag is set),
 * so form state has to live in an inner component that mounts when the modal opens -
 * otherwise it would be initialised once, from an empty store, and never reset between
 * openings.
 */

const MIN_REASON_LENGTH = 10;

interface FormProps {
    case_uid: string,
    onClose: () => void,
    onSubmit: (data: { case_uid: string, reason: string }) => Promise<{ success: boolean, error?: string }>
}

const RejectAttorneyForm = (props: FormProps) => {
    const {case_uid, onClose, onSubmit} = props;
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');
    const [sending, setSending] = useState(false);

    const submit = async (): Promise<void> => {
        const trimmed = reason.trim();
        if (trimmed.length < MIN_REASON_LENGTH) {
            return setError(`Please tell us why, in at least ${MIN_REASON_LENGTH} characters.`);
        }
        setError('');
        setSending(true);
        const result = await onSubmit({case_uid, reason: trimmed});
        setSending(false);
        if (!result.success) {
            setError(result.error || 'We could not send your request. Please try again.');
        }
    };

    const description: any = <span className='m-0'>
        We will ask this lawyer to release your case. They have {UNMATCH_WINDOW_HOURS} business
        hours&nbsp;&mdash; Monday to Friday, 9:00 AM to 5:00 PM only, so a request made on a Friday
        afternoon or over a weekend will not run out until a weekday&nbsp;&mdash; to accept or object.
        <span className='mt-2 d-block'>
            Either way your case is then canceled and your payment is refunded in full, and you can
            resubmit it to be matched with a different lawyer.
        </span>
    </span>;

    return (
        <Modals.Danger open={true}
                       title={'Reject this lawyer?'}
                       cancelable={{
                           buttonTitle: 'Never Mind',
                           action: onClose
                       }}
                       submit={{
                           buttonTitle: sending ? 'Sending...' : 'Reject Lawyer',
                           action: submit,
                           disabled: sending
                       }}
                       icon={'times'}
                       mainBtnColor='secondary'
                       secondaryBtnColor='secondary'
                       description={description}>
            <Fragment>
                <TextField
                    id="reject-attorney-reason"
                    label="Reason"
                    value={reason}
                    error={!!error}
                    helperText={error}
                    onChange={(e: any) => setReason(e.target.value)}
                    fullWidth
                    multiline
                    rows="4"
                    variant="outlined"
                    inputProps={{
                        maxLength: 2000
                    }}
                />
            </Fragment>
        </Modals.Danger>
    );
};

interface OwnProps {
}

interface ConnectStateProps {
    Modals?: any
}

interface ConnectDispatchProps {
    showRejectAttorneyModal: (bool: boolean) => void
    rejectAttorney: (data: { case_uid: string, reason: string }) => Promise<{ success: boolean, error?: string }>
}

const RejectAttorney = (props: any) => {
    const {caseRejectAttorneyModal} = props.Modals;
    if (!caseRejectAttorneyModal) return null;

    const split = window.location.pathname.split('/');
    const case_uid = split[split.length - 1];

    return <RejectAttorneyForm case_uid={case_uid}
                               onClose={() => props.showRejectAttorneyModal(false)}
                               onSubmit={props.rejectAttorney}/>;
};

type ConnectStateMapper = MapStateToProps<ConnectStateProps, OwnProps>

const mapStateToProps: ConnectStateMapper = (state: any) => {
    const {Modals} = state;
    return {Modals}
};

type ConnectDispatchMapper = MapDispatchToProps<ConnectDispatchProps, OwnProps>

const mapDispatchToProps: ConnectDispatchMapper = (dispatch: any) => {
    return {
        showRejectAttorneyModal: (data: boolean) => dispatch(showRejectAttorneyModal(data)),
        rejectAttorney: (data: { case_uid: string, reason: string }) => dispatch(rejectAttorney(data))
    }
};

export {RejectAttorneyForm, MIN_REASON_LENGTH};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RejectAttorney));
